import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, HostListener, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonComponent } from '../../../components/ui/button/button';
import { LabelComponent } from '../../../components/form/label/lable';
import { InputFieldComponent } from '../../../components/form/input/input-field';
import { TextAreaComponent } from '../../../components/form/input/text-area';
import { SelectComponent } from '../../../components/form/select/select';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../../shared/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { AuthStateService } from '../../../shared/states/auth.state';
import { EMAIL_REGEXP } from '../../../shared/validators/email.validator';
import { EmojiPickerComponent } from '../../../components/ui/emoji-picker/emoji-picker';

type ModalStep = 'email' | 'otp';

@Component({
  selector: 'app-setting',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ButtonComponent,
    LabelComponent,
    InputFieldComponent,
    TextAreaComponent,
    SelectComponent,
    FormsModule,
    EmojiPickerComponent
  ],
  templateUrl: './setting.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Setting implements OnInit, OnDestroy {

  @ViewChild('bioTextArea') bioTextArea!: TextAreaComponent;

  constructor(
    private userService: UserService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private authState: AuthStateService,
    private router: Router,
  ) { }

  // ── Profile fields ─────────────────────────────────────────────────────────
  userName = '';
  displayName = ''
  bio = '';
  selectedStatus = '';
  currentEmail = '';
  loading = false;
  maxBioLength = 250;
  bioError = '';
  showBioEmojiPicker = false;
  bioEmojiDDPos = { top: 0, left: 0 };
  private bioSelStart = 0;
  private bioSelEnd = 0;

  // ── Change Email modal ─────────────────────────────────────────────────────
  showEmailModal = false;
  modalStep: ModalStep = 'email';
  newEmail = '';
  otp = '';
  otpDigits = ['', '', '', '', '', ''];
  emailError = '';
  otpError = '';
  emailLoading = false;
  otpLoading = false;
  resendCooldown = 0;
  private resendTimer?: ReturnType<typeof setInterval>;

  // ── Change Password modal ──────────────────────────────────────────────────
  showPasswordModal = false;
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  showCurrentPw = false;
  showNewPw = false;
  showConfirmPw = false;
  pwCurrentError = '';
  pwNewError = '';
  pwConfirmError = '';
  pwLoading = false;

  // ── Options ────────────────────────────────────────────────────────────────
  statusOptions = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'ON HOLIDAY', label: 'On Holiday' },
  ];

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void { this.getProfile(); }

  ngOnDestroy(): void { clearInterval(this.resendTimer); }

  // ── Dropdown outside click ─────────────────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.showBioEmojiPicker && !target.closest('[data-bio-emoji-section]')) {
      this.showBioEmojiPicker = false;
      this.cdr.markForCheck();
    }
  }

  // ── Profile ────────────────────────────────────────────────────────────────
  getProfile() {
    this.userService.getUserProfile().subscribe({
      next: (res: any) => {
        if (res.success) {
          const user = res.data;
          this.userName = user.userName;
          this.displayName = user.displayName;
          this.bio = user.bio || '';
          this.currentEmail = user.email || '';
          this.selectedStatus = user.status || '';
          this.cdr.markForCheck();
        }
      },
      error: (err) => console.error(err)
    });
  }

  updateProfile() {
    this.validateBio();
    if (this.bioError) return;
    this.loading = true;
    this.cdr.markForCheck();
    const payload = {
      userName: this.userName,
      displayName: this.displayName,
      bio: this.bio ?? '',
      status: this.selectedStatus
    };
    this.userService.updateUserProfile(payload).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.cdr.markForCheck();
        if (res.success) {
          this.toastr.success('Profile updated successfully');
          const userName = this.authState.getAuthSnapshot()?.user?.userName ?? this.userName;
          this.router.navigate(['/profile', userName, 'post']);
        }
      },
      error: (err) => {
        this.loading = false;
        this.cdr.markForCheck();
        this.toastr.error(err?.error?.message || 'Failed to update profile');
      }
    });
  }

  validateBio() {
    this.bioError = this.bio && this.bio.length > this.maxBioLength
      ? `You have exceeded the maximum amount of characters.` : '';
    this.cdr.markForCheck(); 
  }

  toggleBioEmojiPicker(btn: HTMLElement): void {
    const willOpen = !this.showBioEmojiPicker;
    const el = this.bioTextArea?.textareaRef?.nativeElement;
    if (el) {
      this.bioSelStart = el.selectionStart ?? this.bio.length;
      this.bioSelEnd = el.selectionEnd ?? this.bio.length;
    }
    this.showBioEmojiPicker = willOpen;
    if (willOpen) {
      // Position is computed in the containing `.relative` wrapper's own
      // coordinate space (not the viewport) so the panel is `position: absolute`
      // and scrolls together with the form instead of staying pinned in place.
      const container = btn.closest('[data-bio-emoji-section]') as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const panelWidth = 288; // w-72
      const panelHeight = 260; // approx. tabs + emoji grid + padding
      const viewportLeft = Math.max(8, Math.min(btnRect.right - panelWidth, window.innerWidth - panelWidth - 8));
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const viewportTop = spaceBelow < panelHeight && btnRect.top > panelHeight
        ? Math.max(8, btnRect.top - panelHeight - 4)
        : btnRect.bottom + 4;
      this.bioEmojiDDPos = {
        top: -110.5,
        left: viewportLeft - containerRect.left
      };
    }
    this.cdr.markForCheck();
  }

  pickBioEmoji(emoji: string): void {
    // insertAtCursor() fires a native 'input' event, which propagates through
    // ngModel to update `bio` and call validateBio() automatically.
    this.bioTextArea.insertAtCursor(emoji, this.bioSelStart, this.bioSelEnd);
    this.showBioEmojiPicker = false;
    this.cdr.markForCheck();
  }

  // ── Change Email Modal ─────────────────────────────────────────────────────

  openEmailModal() {
    this.showEmailModal = true;
    this.modalStep = 'email';
    this.newEmail = '';
    this.otp = '';
    this.otpDigits = ['', '', '', '', '', ''];
    this.emailError = '';
    this.otpError = '';
    this.emailLoading = false;
    this.otpLoading = false;
    this.resendCooldown = 0;
    clearInterval(this.resendTimer);
    this.cdr.markForCheck();
  }

  closeEmailModal() {
    this.showEmailModal = false;
    clearInterval(this.resendTimer);
    this.cdr.markForCheck();
  }

  submitNewEmail() {
    this.emailError = '';
    if (!this.newEmail.trim()) {
      this.emailError = 'Please enter your new email address';
      this.cdr.markForCheck();
      return;
    }
    if (!EMAIL_REGEXP.test(this.newEmail.trim())) {
      this.emailError = 'Please enter a valid email address';
      this.cdr.markForCheck();
      return;
    }
    if (this.newEmail.trim().toLowerCase() === this.currentEmail.toLowerCase()) {
      this.emailError = 'New email must be different from your current email';
      this.cdr.markForCheck();
      return;
    }
    this.emailLoading = true;
    this.cdr.markForCheck();
    this.userService.requestEmailChange(this.newEmail.trim()).subscribe({
      next: () => {
        this.emailLoading = false;
        this.modalStep = 'otp';
        this.otpDigits = ['', '', '', '', '', ''];
        this.otpError = '';
        this.startResendCooldown();
        this.cdr.markForCheck();
        setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
      },
      error: (err: any) => {
        this.emailLoading = false;
        this.emailError = err?.error?.message || 'Failed to send verification code. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/\D/g, '').slice(-1);
    this.otpDigits[index] = val;
    this.otpError = '';
    if (val && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
    this.cdr.markForCheck();
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) ?? '';
    for (let i = 0; i < 6; i++) {
      this.otpDigits[i] = paste[i] ?? '';
    }
    const lastFilled = Math.min(paste.length, 5);
    setTimeout(() => document.getElementById(`otp-${lastFilled}`)?.focus(), 0);
    this.cdr.markForCheck();
  }

  get otpComplete(): boolean {
    return this.otpDigits.every(d => d !== '');
  }

  get otpValue(): string {
    return this.otpDigits.join('');
  }

  submitOtp() {
    if (!this.otpComplete) {
      this.otpError = 'Please enter all 6 digits';
      this.cdr.markForCheck();
      return;
    }
    this.otpLoading = true;
    this.otpError = '';
    this.cdr.markForCheck();
    this.userService.verifyEmailChange(this.otpValue).subscribe({
      next: (res: any) => {
        this.otpLoading = false;
        this.currentEmail = res?.data?.email ?? this.newEmail;
        this.closeEmailModal();
        this.toastr.success('Email address updated successfully');
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.otpLoading = false;
        this.otpDigits = ['', '', '', '', '', ''];
        this.otpError = err?.error?.message || 'Invalid or expired code. Please try again.';
        this.cdr.markForCheck();
        setTimeout(() => document.getElementById('otp-0')?.focus(), 50);
      }
    });
  }

  goBackToEmail() {
    this.modalStep = 'email';
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
    this.cdr.markForCheck();
  }

  resendOtp() {
    if (this.resendCooldown > 0 || this.emailLoading) return;
    this.emailLoading = true;
    this.otpDigits = ['', '', '', '', '', ''];
    this.otpError = '';
    this.cdr.markForCheck();
    this.userService.requestEmailChange(this.newEmail.trim()).subscribe({
      next: () => {
        this.emailLoading = false;
        this.startResendCooldown();
        this.toastr.info('A new verification code has been sent');
        this.cdr.markForCheck();
        setTimeout(() => document.getElementById('otp-0')?.focus(), 100);
      },
      error: (err: any) => {
        this.emailLoading = false;
        this.otpError = err?.error?.message || 'Failed to resend code. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }

  // ── Change Password Modal ─────────────────────────────────────────────────

  openPasswordModal() {
    this.showPasswordModal = true;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.showCurrentPw = false;
    this.showNewPw = false;
    this.showConfirmPw = false;
    this.pwCurrentError = '';
    this.pwNewError = '';
    this.pwConfirmError = '';
    this.pwLoading = false;
    this.cdr.markForCheck();
  }

  closePasswordModal() {
    this.showPasswordModal = false;
    this.cdr.markForCheck();
  }

  stripSpaces(value: string): string {
    return (value ?? '').replace(/\s/g, '');
  }

  validatePasswordFields(): boolean {
    this.pwCurrentError = '';
    this.pwNewError = '';
    this.pwConfirmError = '';
    let valid = true;
    if (!this.currentPassword) {
      this.pwCurrentError = 'Current password is required';
      valid = false;
    }
    if (!this.newPassword) {
      this.pwNewError = 'New password is required';
      valid = false;
    } else if (this.newPassword.length < 8) {
      this.pwNewError = 'Password must be at least 8 characters';
      valid = false;
    } else if (this.newPassword === this.currentPassword) {
      this.pwNewError = 'New password must be different from current password';
      valid = false;
    }
    if (!this.confirmPassword) {
      this.pwConfirmError = 'Please confirm your new password';
      valid = false;
    } else if (this.newPassword && this.confirmPassword !== this.newPassword) {
      this.pwConfirmError = 'Passwords do not match';
      valid = false;
    }
    this.cdr.markForCheck();
    return valid;
  }

  get passwordStrength(): { score: number; label: string; color: string } {
    const pw = this.newPassword;
    if (!pw) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { score, label: 'Weak',   color: '#ef4444' };
    if (score <= 3) return { score, label: 'Fair',   color: '#f59e0b' };
    if (score === 4) return { score, label: 'Good',  color: '#3b82f6' };
    return               { score, label: 'Strong',   color: '#22c55e' };
  }

  submitChangePassword() {
    if (!this.validatePasswordFields()) return;
    this.pwLoading = true;
    this.cdr.markForCheck();
    this.userService.changePassword(this.currentPassword, this.newPassword, this.confirmPassword).subscribe({
      next: (res: any) => {
        this.pwLoading = false;
        this.closePasswordModal();
        const others = Number(res?.data?.otherSessionsSignedOut ?? 0);
        this.toastr.success(others > 0
          ? `Password changed. You've been signed out on ${others} other device${others === 1 ? '' : 's'}.`
          : 'Password changed successfully');
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.pwLoading = false;
        const msg = err?.error?.message || 'Failed to change password. Please try again.';
        // Show on relevant field
        if (msg.toLowerCase().includes('current')) {
          this.pwCurrentError = msg;
        } else if (msg.toLowerCase().includes('match')) {
          this.pwConfirmError = msg;
        } else {
          this.pwNewError = msg;
        }
        this.cdr.markForCheck();
      }
    });
  }

  private startResendCooldown(seconds = 60) {
    clearInterval(this.resendTimer);
    this.resendCooldown = seconds;
    this.cdr.markForCheck();
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      this.cdr.markForCheck();
      if (this.resendCooldown <= 0) clearInterval(this.resendTimer);
    }, 1000);
  }
}
